// AI answers for the widget (ADR 0005).
//
// Talks to the KB app host's POST /api/ask/embed, authorized by the same per-app
// share token the read path uses. The endpoint answers in one of two shapes:
//
//  • application/json — nothing retrieved, or no model configured. One terminal
//    "no answer, here are the articles" reply.
//  • text/event-stream — `meta` (sources), then `delta`s, then `done`.
//
// Both are normalized into the same AskEvent sequence so App.tsx has one thing
// to render. Any failure ends the sequence with an `error` event; the widget
// then falls back to its client-side result list, because it renders inside
// someone else's product and must stay useful when the KB host is unreachable.

import type { WidgetConfig } from "./data";
import { resolveAuthToken } from "./viewer";

export type AskSource = { slug: string; title: string };

export type AskEvent =
  | { type: "sources"; sources: AskSource[] }
  | { type: "delta"; text: string }
  | { type: "done"; grounded: boolean }
  | { type: "error"; error: string };

type SseEvent = { event: string; data: string };

/**
 * Split whatever has arrived so far into complete SSE events, returning the
 * trailing partial event to be prepended to the next chunk. Pure, so the
 * framing is testable without a network.
 */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  // Events are separated by a blank line; anything after the last one is a
  // partial event still being written.
  const parts = buffer.split(/\n\n/);
  const rest = parts.pop() ?? "";
  for (const part of parts) {
    let event = "message";
    const data: string[] = [];
    for (const line of part.split("\n")) {
      if (line.startsWith("event:")) event = line.slice(6).trim();
      else if (line.startsWith("data:")) data.push(line.slice(5).trim());
    }
    if (data.length) events.push({ event, data: data.join("\n") });
  }
  return { events, rest };
}

/** Map one server event onto an AskEvent, or null for anything unrecognized. */
function toAskEvent(ev: SseEvent): AskEvent | null {
  let payload: Record<string, unknown>;
  try {
    payload = JSON.parse(ev.data) as Record<string, unknown>;
  } catch {
    return null;
  }
  switch (ev.event) {
    case "meta":
      return { type: "sources", sources: (payload.sources ?? []) as AskSource[] };
    case "delta":
      return { type: "delta", text: String(payload.text ?? "") };
    case "done":
      return { type: "done", grounded: Boolean(payload.grounded) };
    case "error":
      return { type: "error", error: String(payload.error ?? "Something went wrong.") };
    default:
      return null;
  }
}

/**
 * Ask a question, calling `onEvent` as the answer arrives. Resolves when the
 * sequence is finished; never rejects — a failure is delivered as an `error`
 * event so the caller has one code path.
 */
export async function askQuestion(
  cfg: WidgetConfig,
  question: string,
  onEvent: (e: AskEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (!cfg.askUrl || !cfg.token) {
    // Retrieval is scoped by the share token; without one there is nothing to
    // authorize an answer with.
    onEvent({ type: "error", error: "unavailable" });
    return;
  }
  // Asked for immediately before the request, so an answer is grounded in what
  // the reader can open right now rather than in a token that expired while the
  // panel was open (see ./viewer.ts).
  const viewerToken = await resolveAuthToken();
  try {
    const res = await fetch(cfg.askUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      signal,
      // The reader's host token rides along when there is one: an answer must
      // be grounded in the articles THEY can open, or it quotes a colleague's.
      // The server verifies it and ignores anything it cannot (services/
      // hostIdentity.ts), which is the anonymous scope — what this asked for
      // before the token existed.
      body: JSON.stringify({
        token: cfg.token,
        question,
        lang: "en",
        ...(viewerToken ? { authToken: viewerToken } : {}),
      }),
    });

    if (!res.ok) {
      onEvent({ type: "error", error: `HTTP ${res.status}` });
      return;
    }

    // Terminal JSON reply: no matches, or no model configured.
    if ((res.headers.get("content-type") ?? "").includes("application/json")) {
      const body = (await res.json()) as { sources?: AskSource[] };
      onEvent({ type: "sources", sources: body.sources ?? [] });
      onEvent({ type: "done", grounded: false });
      return;
    }

    if (!res.body) {
      onEvent({ type: "error", error: "no stream" });
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseSse(buffer);
      buffer = rest;
      for (const ev of events) {
        const mapped = toAskEvent(ev);
        if (mapped) onEvent(mapped);
      }
    }
  } catch (e) {
    // An abort is the reader moving on, not a failure worth reporting.
    if (signal?.aborted) return;
    onEvent({ type: "error", error: e instanceof Error ? e.message : "Failed to ask" });
  }
}
